import { MOCK_DATA_URL } from '../../config/appConfig';
import { uid } from '../../shared/format';
import type { ClientMessage, ServerMessage } from '../protocol/messages';
import type { QuoteField, Timeframe } from '../protocol/types';
import { chartStore } from '../stores/chartStore';
import { newsStore } from '../stores/newsStore';
import { quoteStore } from '../stores/quoteStore';
import type { MarketDataTransport } from '../transport/MarketDataTransport';
import { WorkerMarketDataTransport } from '../transport/WorkerMarketDataTransport';

type QuoteSubscription = { ownerId: string; requestId: string; symbols: string[]; fields: QuoteField[] };
type BarSubscription = { ownerId: string; requestId: string; symbol: string; timeframe: Timeframe; limit: number };
type NewsSubscription = { ownerId: string; requestId: string; symbols?: string[]; limit: number };

class MarketDataRuntime {
  private transport: MarketDataTransport;
  private started = false;
  private quoteRequests = new Map<string, QuoteSubscription>();
  private barRequests = new Map<string, BarSubscription>();
  private newsRequests = new Map<string, NewsSubscription>();
  private cleanupTransportListeners: Array<() => void> = [];
  private heartbeat?: ReturnType<typeof window.setInterval>;

  constructor(transport: MarketDataTransport) {
    this.transport = transport;
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    this.cleanupTransportListeners = [
      this.transport.onMessage((message) => this.handleServerMessage(message)),
      this.transport.onStatus((_status, message) => quoteStore.setStatus(false, message))
    ];
    this.transport.open();
    this.post({ type: 'client.connect', payload: { config: { mockDataUrl: MOCK_DATA_URL } } });
    this.heartbeat = window.setInterval(() => {
      this.post({ type: 'client.ping', payload: { time: Date.now() } });
    }, 15_000);
  }

  stop(): void {
    this.post({ type: 'client.disconnect' });
    this.cleanupTransportListeners.forEach((cleanup) => cleanup());
    this.cleanupTransportListeners = [];
    if (this.heartbeat) window.clearInterval(this.heartbeat);
    this.transport.close();
    this.started = false;
  }

  subscribeQuotes(ownerId: string, symbols: string[], fields: QuoteField[]): () => void {
    this.unsubscribeQuotes(ownerId);
    const requestId = uid('quotes');
    const subscription = { ownerId, requestId, symbols, fields };
    this.quoteRequests.set(ownerId, subscription);
    this.post({ type: 'client.subscribeQuotes', payload: { requestId, symbols, fields } });
    return () => this.unsubscribeQuotes(ownerId, requestId);
  }

  subscribeBars(ownerId: string, symbol: string, timeframe: Timeframe, limit = 260): () => void {
    this.unsubscribeBars(ownerId);
    const requestId = uid('bars');
    const subscription = { ownerId, requestId, symbol, timeframe, limit };
    this.barRequests.set(ownerId, subscription);
    this.post({ type: 'client.subscribeBars', payload: { requestId, symbol, timeframe, limit } });
    return () => this.unsubscribeBars(ownerId, requestId);
  }

  subscribeNews(ownerId: string, symbols?: string[], limit = 100): () => void {
    this.unsubscribeNews(ownerId);
    const requestId = uid('news');
    const subscription = { ownerId, requestId, symbols, limit };
    this.newsRequests.set(ownerId, subscription);
    this.post({ type: 'client.subscribeNews', payload: { requestId, symbols, limit } });
    return () => this.unsubscribeNews(ownerId, requestId);
  }

  reloadMockData(): void {
    this.post({ type: 'client.reloadMock', payload: { mockDataUrl: MOCK_DATA_URL } });
    window.setTimeout(() => this.replaySubscriptions(), 0);
  }

  private unsubscribeQuotes(ownerId: string, requestId?: string): void {
    const existing = this.quoteRequests.get(ownerId);
    if (!existing) return;
    if (requestId && existing.requestId !== requestId) return;
    this.quoteRequests.delete(ownerId);
    this.post({ type: 'client.unsubscribeQuotes', payload: { requestId: existing.requestId } });
  }

  private unsubscribeBars(ownerId: string, requestId?: string): void {
    const existing = this.barRequests.get(ownerId);
    if (!existing) return;
    if (requestId && existing.requestId !== requestId) return;
    this.barRequests.delete(ownerId);
    this.post({ type: 'client.unsubscribeBars', payload: { requestId: existing.requestId } });
  }

  private unsubscribeNews(ownerId: string, requestId?: string): void {
    const existing = this.newsRequests.get(ownerId);
    if (!existing) return;
    if (requestId && existing.requestId !== requestId) return;
    this.newsRequests.delete(ownerId);
    this.post({ type: 'client.unsubscribeNews', payload: { requestId: existing.requestId } });
  }

  private replaySubscriptions(): void {
    this.quoteRequests.forEach((sub) => this.post({ type: 'client.subscribeQuotes', payload: { requestId: sub.requestId, symbols: sub.symbols, fields: sub.fields } }));
    this.barRequests.forEach((sub) => this.post({ type: 'client.subscribeBars', payload: { requestId: sub.requestId, symbol: sub.symbol, timeframe: sub.timeframe, limit: sub.limit } }));
    this.newsRequests.forEach((sub) => this.post({ type: 'client.subscribeNews', payload: { requestId: sub.requestId, symbols: sub.symbols, limit: sub.limit } }));
  }

  private post(message: ClientMessage): void {
    this.transport.send(message);
  }

  private handleServerMessage(message: ServerMessage): void {
    switch (message.type) {
      case 'server.ready':
        quoteStore.setUniverse(message.payload.universe);
        quoteStore.setIndexes(message.payload.indexes);
        this.replaySubscriptions();
        break;
      case 'server.status':
        quoteStore.setStatus(message.payload.connected, message.payload.message);
        break;
      case 'server.quoteSnapshot':
        quoteStore.applySnapshot(message.payload.quotes, message.seq);
        break;
      case 'server.quoteDeltaBatch':
        quoteStore.applyPatch(message.payload.updates, message.seq, message.payload.dropped);
        break;
      case 'server.barSnapshot':
        chartStore.setSnapshot(message.payload.symbol, message.payload.timeframe, message.payload.bars);
        break;
      case 'server.barUpdate':
        chartStore.appendBar(message.payload.bar);
        break;
      case 'server.newsSnapshot':
        newsStore.setSnapshot(message.payload.items);
        break;
      case 'server.newsUpdate':
        newsStore.push(message.payload.item);
        break;
      case 'server.error':
        console.error(message.payload.code, message.payload.message);
        break;
      case 'server.pong':
        break;
    }
  }
}

export const marketDataRuntime = new MarketDataRuntime(
  new WorkerMarketDataTransport(() => new Worker(new URL('../worker/marketData.worker.ts', import.meta.url), { type: 'module' }))
);

export function startMarketDataRuntime(): void {
  marketDataRuntime.start();
}
